<template>
  <div id="page-risk-codes-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page
            :card-title="pageTitle"
            :card-subtitle="`Description: ${model.RiskCodeDescription}`"
            :switch-model="model.IsEnabled"
        >
          <vue-element-loading :active="loading"></vue-element-loading>
          <vue-form-generator
              v-if="riskCodeReferences.length"
              class="pt-4"
              :schema="schema"
              :model="model"
              :options="formOptions"
              @validated="isValid"
              ref="form"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import CardPage from '../../components/Views/CardPage';
import FormModelMixin from '../../../components/mixins/FormModelMixin';

export default {
  name: "RiskCodeForm",
  props: {
    value: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    pageTitle: {
      type: String,
    }
  },
  mixins: [FormModelMixin],
  components: {
    CardPage
  },
  data() {
    return {
      selectedReferences: [],
      model: {
        RiskCodeText: '',
        RiskCodeDescription: '',
        RiskCodeReferenceOne_FK: null,
        RiskCodeReferenceTwo_FK: null,
        RiskCodeReferenceThree_FK: null,
        RiskCodeReferenceFour_FK: null,
        RiskCodeDescriptionFour: '',
        RiskCodeReferenceFive_FK: null,
        RiskCodeReferenceSix_FK: null,
        RiskCodeDescriptionSix: '',
      },
      schema: {
        groups: [
          {
            styleClasses: "w-full",
            fields: [
              {
                type: "input",
                disabled: this.readonly,
                inputType: "text",
                styleClasses: "w-full test-class-three",
                label: "Risk Code",
                model: "RiskCodeText",
                validator: ['required', 'string'],
                required: true,
              },
            ]
          },
          {
            styleClasses: "w-full",
            legend: 'References',
            fields: [
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-full",
                label: "Risk Code Reference 1",
                model: "RiskCodeReferenceOne_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferencesWithDescription.filter(({type}) => type === 'ONE'),
                onChanged: (model) => {
                  model.RiskCodeReferenceThree_FK = null
                },
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
            ]
          },
          {
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-full",
                label: "Risk Code Reference 2",
                model: "RiskCodeReferenceTwo_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferencesWithDescription.filter(({type}) => type === 'TWO'),
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
            ]
          },
          {
            styleClasses: "w-full",
            fields: [
              {
                type: "vueMultiSelect",
                inputType: "text",
                disabled: () => !this.model.RiskCodeReferenceOne_FK || !this.model.RiskCodeReferenceTwo_FK || this.readonly,
                styleClasses: "w-full",
                label: "Risk Code Reference 3",
                model: "RiskCodeReferenceThree_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferenceThreeValues,
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
            ]
          },
          {
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Risk Code Reference 4",
                model: "RiskCodeReferenceFour_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferences.filter(({type}) => type === 'FOUR'),
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
              {
                type: "input",
                disabled: this.readonly,
                inputType: "text",
                styleClasses: "w-2/3",
                label: "Risk Code Description 4",
                model: "RiskCodeDescriptionFour",
                validator: 'string',
              },
            ]
          },
          {
            styleClasses: "w-full",
            fields: [
              {
                type: "vueMultiSelect",
                inputType: "text",
                disabled: () => !this.model.RiskCodeReferenceThree_FK || this.readonly,
                styleClasses: "w-full",
                label: "Risk Code Reference 5",
                model: "RiskCodeReferenceFive_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferencesWithDescription.filter(({type, RiskCodeParentReference_FK}) =>
                    this.model.RiskCodeReferenceThree_FK
                    && RiskCodeParentReference_FK == this.model.RiskCodeReferenceThree_FK.id
                    && type === 'FIVE'
                ),
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
            ]
          },
          {
            styleClasses: "w-full",
            fields: [
              {
                disabled: this.readonly,
                type: "vueMultiSelect",
                inputType: "text",
                styleClasses: "w-1/3",
                label: "Risk Code Reference 6",
                model: "RiskCodeReferenceSix_FK",
                required: true,
                validator: 'required',
                values: () => this.riskCodeReferences.filter(({type}) => type === 'SIX'),
                selectOptions: {
                  showLabels: false,
                  hideSelected: true,
                  key: 'id',
                  trackBy: 'id',
                  label: 'name',
                }
              },
              {
                type: "input",
                disabled: this.readonly,
                inputType: "text",
                styleClasses: "w-2/3",
                label: "Risk Code Description 6",
                model: "RiskCodeDescriptionSix",
                required: true,
                validator: ['required', 'string'],
              },
            ]
          }
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      riskCodeReferencesWithDescription: [],
      riskCodeReferences: [],
    };
  },
  computed: {
    riskCodeReferenceThreeValues() {
      return this.riskCodeReferencesWithDescription.filter(({type, RiskCodeParentReference_FK, RiskCodeParentReferenceTwo_FK}) =>
        this.model.RiskCodeReferenceOne_FK
        && RiskCodeParentReference_FK == this.model.RiskCodeReferenceOne_FK.id
        && this.model.RiskCodeReferenceTwo_FK
        && RiskCodeParentReferenceTwo_FK == this.model.RiskCodeReferenceTwo_FK.id
        && type === 'THREE'
      );
    },
    riskCodeDescriptionConcat() {
      const fields = [
        this.model.RiskCodeReferenceOne_FK,
        this.model.RiskCodeReferenceTwo_FK,
        this.model.RiskCodeReferenceThree_FK,
        this.model.RiskCodeReferenceFive_FK,
      ];

      return fields.filter((riskCode) => riskCode && riskCode.Description)
          .map((riskCode) => riskCode.Description)
          .join(' - ');
    }
  },
  methods: {
    async loadReferences() {
      try {
        const { data } = await this.$api.riskCodeReference.list();
        this.riskCodeReferencesWithDescription = await data.data.map(({id, RiskClass, RiskCodeParentReference_FK, RiskCodeParentReferenceTwo_FK, Description, type}) =>
          ({
            id,
            name: `${RiskClass} - ${Description}`,
            type: type.Type,
            Description,
            RiskCodeParentReference_FK,
            RiskCodeParentReferenceTwo_FK
          })
        );

        this.riskCodeReferences = await data.data.map(({id, RiskClass, type}) =>
          ({
            id,
            name: RiskClass,
            type: type.Type
          })
        );
      } catch (e) {
        console.log(e);
      }
    },
    setModelReferencesToObjects() {
      const references = [
        'RiskCodeReferenceOne_FK',
        'RiskCodeReferenceTwo_FK',
        'RiskCodeReferenceThree_FK',
        'RiskCodeReferenceFour_FK',
        'RiskCodeReferenceFive_FK',
        'RiskCodeReferenceSix_FK',
      ];

      references.forEach((ref) => {
        if (!this.model[ref]) {
          return true;
        }

        const refObj = this.riskCodeReferencesWithDescription.find(({id}) => id === parseInt(this.model[ref]));

        this.model[ref] = refObj ? refObj : null;``
      })
    },
    isValid(isValid) {
      this.$emit('validated', isValid);
    },
    validate() {
      return this.$refs.form.validate();
    }
  },
  watch: {
    model: {
      handler(newValue) {
        this.$emit('input', newValue);
      },
      immediate: true,
      deep: true,
    },
    riskCodeDescriptionConcat(value) {
      this.model.RiskCodeDescription = value;
    },
  },
  async created() {
    await this.loadReferences();
  }
};
</script>
